/**
 * Circle geometry for the gimmicks' SVG looks (feature plans 0071, 0072): the stopwatch's ring, the picker's
 * wheel, drum and card fan. Fractions run clockwise from 12 o'clock, `0 … 1` is one turn. Only geometry —
 * how a path is stroked or filled stays in ralphy.css.
 */

export interface Point {
    readonly x: number;
    readonly y: number;
}

/** The point `fraction` of the way round a circle, clockwise from 12 o'clock. */
export function polarPoint(cx: number, cy: number, r: number, fraction: number): Point {
    const angle = fraction * 2 * Math.PI - Math.PI / 2;
    return {x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle)};
}

/**
 * The arc from `from` to `to` (fractions, `to > from`) as a path `d`. The start point is written as short as
 * possible (`M 100 16`), the end with two decimals — the form the stopwatch ring has always written. `null`
 * when the arc is empty; from one full turn on, a full circle (two half arcs — one arc to its own start point
 * would draw nothing).
 */
export function arcPath(cx: number, cy: number, r: number, from: number, to: number): string | null {
    const span = to - from;
    if (!(span > 0)) {
        return null;
    }
    const start = polarPoint(cx, cy, r, from);
    const head = `M ${short(start.x)} ${short(start.y)}`;
    if (span >= 1) {
        const opposite = polarPoint(cx, cy, r, from + 0.5);
        return `${head} A ${r} ${r} 0 1 1 ${fixed(opposite)} A ${r} ${r} 0 1 1 ${fixed(start)}`;
    }
    return `${head} A ${r} ${r} 0 ${span > 0.5 ? 1 : 0} 1 ${fixed(polarPoint(cx, cy, r, to))}`;
}

/** A pie slice from the centre — a wheel segment. `null` when empty; a full turn is the circle's outline (the
 *  caller draws a `<circle>` for a single segment instead, see the wheel look). */
export function sectorPath(cx: number, cy: number, r: number, from: number, to: number): string | null {
    const arc = arcPath(cx, cy, r, from, to);
    if (arc === null || to - from >= 1) {
        return arc;
    }
    return `M ${short(cx)} ${short(cy)} L ${arc.slice(2)} Z`;
}

function short(value: number): string {
    return String(Number(value.toFixed(2)));
}

function fixed({x, y}: Point): string {
    return `${x.toFixed(2)} ${y.toFixed(2)}`;
}
